import { useAtom, useAtomValue } from 'jotai';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';

import { cdnProvidersListParamsAtom, cdnProvidersHydratedAtom } from '@/store';

export interface SearchFields {
  name: string;
  base_url: string;
}

export function useCdnProvidersFilters() {
  const [params, setParams] = useAtom(cdnProvidersListParamsAtom);
  const [, setHydrated] = useAtom(cdnProvidersHydratedAtom);

  // URL state management
  const [urlState, setUrlState] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    search: parseAsString.withDefault(''),
    name: parseAsString,
    base_url: parseAsString,
    date: parseAsString,
  });

  // Initialize hydrated state and sync URL with params
  useMemo(() => {
    if (!urlState.page || !urlState.per_page) return;

    const filters: Record<string, string | undefined> = {};
    if (urlState.search) filters.search = urlState.search;
    if (urlState.name) filters.name = urlState.name;
    if (urlState.base_url) filters.base_url = urlState.base_url;
    if (urlState.date) filters.date = urlState.date;

    setParams({
      page: urlState.page,
      per_page: urlState.per_page,
      filters,
    });

    setHydrated(true);
  }, [urlState.page, urlState.per_page, urlState.search, urlState.name, urlState.base_url, urlState.date, setParams, setHydrated]);

  // Search fields state
  const searchFields: SearchFields = {
    name: params.filters.name || '',
    base_url: params.filters.base_url || '',
  };

  // Search state - local state for input, URL state for actual search
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.search || '');
  const searchTerm = urlState.search || '';
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  const setSearchFields = useCallback((updater: (prev: SearchFields) => SearchFields) => {
    const newFields = updater(searchFields);
    setUrlState({
      name: newFields.name || null,
      base_url: newFields.base_url || null,
      page: 1, // Reset to first page when searching
    });
  }, [searchFields, setUrlState]);

  // Search handler
  const handleSearch = useCallback(() => {
    setUrlState({
      search: localSearchTerm || null,
      page: 1,
    });
  }, [localSearchTerm, setUrlState]);

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    setUrlState({
      search: null,
      page: 1,
    });
  }, [setUrlState]);

  const applyFilters = useCallback(() => {
    setUrlState({
      name: searchFields.name || null,
      base_url: searchFields.base_url || null,
      page: 1,
    });
  }, [searchFields, setUrlState]);

  const clearFilters = useCallback(() => {
    setSearchFields(() => ({ name: '', base_url: '' }));
    setUrlState({
      search: null,
      name: null,
      base_url: null,
      page: 1,
    });
  }, [setSearchFields, setUrlState]);

  const handlePageChange = useCallback((page: number) => {
    setUrlState({ page });
  }, [setUrlState]);

  const handleDateChange = useCallback((value: { start: Date | null; end: Date | null }) => {
    setDateFilter(value);
    
    let dateFilterValue = '';
    if (value.start && value.end) {
      // Format dates as YYYY-MM-DD
      const startDate = value.start.toISOString().split('T')[0];
      const endDate = value.end.toISOString().split('T')[0];
      dateFilterValue = `${startDate},${endDate}`;
    } else if (value.start) {
      dateFilterValue = value.start.toISOString().split('T')[0];
    }
    
    setUrlState({ 
      page: 1, 
      date: dateFilterValue || null 
    });
  }, [setUrlState]);

  return {
    searchTerm: localSearchTerm,
    setSearchTerm: setLocalSearchTerm,
    searchFields,
    setSearchFields,
    dateFilter,
    applyFilters,
    clearFilters,
    handlePageChange,
    handleDateChange,
    handleSearch,
    handleClearSearch,
    hasAnyFilter: Boolean(searchTerm || searchFields.name || searchFields.base_url),
  };
}
